/* global APP  */

import Logger from 'meet-hour-logger';

import { MEDIA_TYPE, VIDEO_TYPE } from '../../../react/features/base/media';
import { getScreenShareParticipantID } from '../../../react/features/base/mtApi';
import {
    getLocalParticipant as getLocalParticipantFromStore,
    getParticipantById,
    getPinnedParticipant,
    isScreenShareParticipantById,
    isSharedThumbnailEnabled,
    pinParticipant
} from '../../../react/features/base/participants';
import { getTrackByMediaTypeAndParticipant } from '../../../react/features/base/tracks';
import { VIDEO_PLAYER_PARTICIPANT_NAME, YOUTUBE_PLAYER_PARTICIPANT_NAME } from '../../../react/features/shared-video/constants';
import { LAYOUTS, participantWithAudioUnmuted, participantWithVideoUnmuted } from '../../../react/features/video-layout';
import UIEvents from '../../../service/UI/UIEvents';

import LargeVideoManager from './LargeVideoManager';
import LocalVideo from './LocalVideo';
import RemoteVideo from './RemoteVideo';
import ScreenShareVideo from './ScreenShareVideo';
import { VIDEO_CONTAINER_TYPE } from './VideoContainer';

// import { getScreenShareParticipantID } from '../../../react/features/base/mtApi';

const logger = Logger.getLogger(__filename);

const remoteVideos = {};
let localVideoThumbnail = null;

let eventEmitter = null;

let largeVideo;

/**
 * A handle for scheduled thumbnail sorting work. It may be either a
 * requestAnimationFrame id or a setTimeout id depending on browser support.
 *
 * @type {{ type: 'raf' | 'timeout', id: number } | null}
 */
let pendingSortHandle = null;

/**
 * Caches the last visible state per thumbnail so we can avoid repeatedly
 * calling play/pause on every global action.
 *
 * @type {Map<string, boolean>}
 */
const thumbnailVisibilityState = new Map();

/**
 * FlipX state of the localVideo.
 */
let localFlipX = null;

/**
 * Handler for local flip X changed event.
 *
 * @param {Object} val
 */
function onLocalFlipXChanged(val) {
    localFlipX = val;
    if (largeVideo) {
        largeVideo.onLocalFlipXChange(val);
    }
}

/**
 * Returns an array of all thumbnails in the filmstrip.
 *
 * @private
 * @returns {Array}
 */
function getAllThumbnails() {
    return [
        ...localVideoThumbnail ? [ localVideoThumbnail ] : [],
        ...Object.values(remoteVideos)
    ];
}

/**
 * Private helper to get the redux representation of the local participant.
 *
 * @private
 * @returns {Object}
 */
function getLocalParticipant() {
    return getLocalParticipantFromStore(APP.store.getState());
}

const VideoLayout = {
    init(emitter) {
        eventEmitter = emitter;

        localVideoThumbnail = new LocalVideo(
            emitter,
            this._updateLargeVideoIfDisplayed.bind(this));

        this.registerListeners();
    },

    /**
     * Registering listeners for UI events in Video layout component.
     *
     * @returns {void}
     */
    registerListeners() {
        eventEmitter.addListener(UIEvents.LOCAL_FLIPX_CHANGED,
            onLocalFlipXChanged);
    },

    /**
     * Cleans up state of this singleton {@code VideoLayout}.
     *
     * @returns {void}
     */
    reset() {
        this._resetLargeVideo();
        this._resetFilmstrip();
    },

    initLargeVideo() {
        this._resetLargeVideo();

        largeVideo = new LargeVideoManager(eventEmitter);
        if (localFlipX) {
            largeVideo.onLocalFlipXChange(localFlipX);
        }
        largeVideo.updateContainerSize();
    },

    /**
     * Sets the audio level of the video elements associated to the given id.
     *
     * @param id - The video identifier in the form it comes from the library.
     * @param lvl - The new audio level to update to.
     */
    setAudioLevel(id, lvl) {
        if (largeVideo && id === largeVideo.id) {
            largeVideo.updateLargeVideoAudioLevel(lvl);
        }
    },

    changeLocalVideo(stream) {
        const localParticipant = getLocalParticipant();
        const localId = localParticipant?.id;

        if (!localId || !localVideoThumbnail) {
            logger.warn('Skipping local video update before local thumbnail is ready.');

            return;
        }

        this.onVideoTypeChanged(localId, stream.videoType);

        try {
            localVideoThumbnail.changeVideo(stream);
        } catch (error) {
            logger.error('Failed to update local video thumbnail stream.', error);
        }

        this._updateLargeVideoIfDisplayed(localId);
    },

    /**
     * Shows/hides local video.
     *
     * @param {boolean} true - To make the local video visible, false - otherwise.
     */
    setLocalVideoVisible(visible) {
        localVideoThumbnail.setVisible(visible);
    },

    onRemoteStreamAdded(stream) {
        const id = stream.getParticipantId();
        const remoteVideo = remoteVideos[id];

        // console.log(`Received a new ${stream.getType()} stream for ${id}`, {
        //     stream,
        //     actionTrack,
        //     id,
        //     remoteVideo
        // });

        if (!remoteVideo) {
            logger.debug('No remote video element to add stream');

            return;
        }

        remoteVideo.addRemoteStreamElement(stream);

        this.onVideoMute(id);
        remoteVideo.updateView();
        this.sortThumbnails();

    },

    onScreenShareStreamAdded(stream) {
        const id = stream.getSourceName();
        const remoteVideo = remoteVideos[id];

        // console.log(`Received a new ${stream.getType()} stream for ${id}`, {
        //     stream,
        //     actionTrack,
        //     id,
        //     remoteVideo,
        //     remoteVideos
        // });

        if (!remoteVideo) {
            logger.debug('No remote video element to add stream');

            return;
        }

        remoteVideo.addRemoteStreamElement(stream);

        this.onVideoMute(id);
        remoteVideo.updateView();
        this.sortThumbnails();

    },

    onRemoteStreamRemoved(stream) {
        const id = stream.getParticipantId();
        const remoteVideo = remoteVideos[id];

        // Remote stream may be removed after participant left the conference.
        if (remoteVideo) {
            remoteVideo.removeRemoteStreamElement(stream);
            remoteVideo.updateView();
        }
        this.sortThumbnails();

        this.updateVideoMutedForNoTracks(id);
    },

    /**
     * FIXME get rid of this method once muted indicator are reactified (by
     * making sure that user with no tracks is displayed as muted ).
     *
     * If participant has no tracks will make the UI display muted status.
     *
     * @param {string} participantId
     */
    updateVideoMutedForNoTracks(participantId) {
        const participant = APP.conference.getParticipantById(participantId);

        if (participant && !participant.getTracksByMediaType('video').length) {
            APP.UI.setVideoMuted(participantId);
        }
    },

    /**
     * Return the type of the remote video.
     *
     * @param id - The id for the remote video.
     * @returns {string} The video type video or screen.
     */
    getRemoteVideoType(id) {
        const state = APP.store.getState();
        const isScreenShare = isScreenShareParticipantById(state, id);

        if (isScreenShare) {
            return VIDEO_TYPE.DESKTOP;
        }

        const videoTrack = getTrackByMediaTypeAndParticipant(state['features/base/tracks'], MEDIA_TYPE.VIDEO, id);

        // Return the video type or default to CAMERA if undefined
        return videoTrack?.videoType || VIDEO_TYPE.CAMERA;
    },

    isPinned(id) {
        return id === this.getPinnedId();
    },

    getPinnedId() {
        const { id } = getPinnedParticipant(APP.store.getState()) || {};

        return id || null;
    },

    /**
     * Triggers a thumbnail to pin or unpin itself.
     *
     * @param {number} videoNumber - The index of the video to toggle pin on.
     * @private
     */
    togglePin(videoNumber) {
        const videos = getAllThumbnails();
        const videoView = videos[videoNumber];

        videoView && videoView.togglePin();
    },

    /**
     * Callback invoked to update display when the pin participant has changed.
     *
     * @paramn {string|null} pinnedParticipantID - The participant ID of the
     * participant that is pinned or null if no one is pinned.
     * @returns {void}
     */
    onPinChange(pinnedParticipantID) {
        getAllThumbnails().forEach(thumbnail => {
            // Call focus for backward compatibility
            thumbnail.focus(pinnedParticipantID === thumbnail.getId());

            // Also trigger updateView to ensure state-based pinning logic is applied
            thumbnail.updateView();
            this.sortThumbnails();
        });
    },

    /**
     * Callback invoked to re-sort thumbnails when a participant has raised hand.
     *
     * @returns {void}
     */
    onRaisedHandUpdate() {
        this.sortThumbnails();
    },

    /**
     * Creates a participant container for the given id.
     *
     * @param {Object} participant - The redux representation of a remote
     * participant.
     * @returns {void}
     */
    addRemoteParticipantContainer(participant) {
        if (!participant || participant.local) {
            return;
        } else if (participant.isFakeParticipant && participant.fakeParticipant) {

            // const thumbnailComponent = new SharedVideoThumb(participant);

            // this.addRemoteVideoContainer(participant.id, thumbnailComponent);

            return;
        }

        const id = participant.id;
        let mHParticipant = APP.conference.getParticipantById(id);

        if (!mHParticipant) {
            mHParticipant = {
                getId: () => id,
                getDisplayName: () => participant.name || 'Fake Participant'
            };
        }
        const remoteVideo = new RemoteVideo(mHParticipant, participant.isFakeParticipant);

        this.addRemoteVideoContainer(id, remoteVideo);
        this.updateVideoMutedForNoTracks(id);
    },

    addScreenShareParticipantContainer(participant) {
        // const fakeParticipant = fakeParticipantObj;

        // console.log('addScreenShareParticipantContainer called - participant:', participant, 'fakeParticipant:', fakeParticipant);

        // console.log('addScreenShareParticipantContainer Participant details - id:', participant.id, 'local:', participant.local, 'isFakeParticipant:', participant.isFakeParticipant);

        // Skip regular local participants (not screen shares)
        if (participant.local && !participant.isFakeParticipant) {
            // console.log('Skipping regular local participant');

            return;
        }


        // Handle all other participants including screen shares (LocalScreenShare, RemoteScreenShare)
        // Screen shares will be treated as RemoteVideo instances
        const id = participant.id;

        // const isScreenShare = participant.isFakeParticipant === 'LocalScreenShare'
        //  || participant.isFakeParticipant === 'RemoteScreenShare';

        // For screen share fake participants, get the actual participant
        const actualIDparticipant = getScreenShareParticipantID(id);
        const mHParticipant = APP.conference.getParticipantById(actualIDparticipant);

        // console.log('Screen share fake participant - ID:', id, 'actualParticipantID:', actualIDparticipant, 'mHParticipant:', mHParticipant);


        // Create ScreenShareVideo for both screen shares and regular participants
        const remoteVideo = new ScreenShareVideo(mHParticipant, participant);

        // comprehensive debug logging information
        // console.log('Creating ScreenShareVideo - ID:', id, 'mHParticipant:', mHParticipant, 'isFakeParticipant:', participant.isFakeParticipant, 'remoteVideo:', remoteVideo);

        this.addRemoteVideoContainer(id, remoteVideo);
        this.updateVideoMutedForNoTracks(id);
    },

    /**
     * Sorts thumbnails in the following priority order:
     * 1. Moderator Pin
     * 2. Screen sharing
     * 3. Raise Hand
     * 4. Dominant Speaker
     * 5. Pin
     * 6. Moderators
     * 7. Video cameras if available
     * 8. Audio if available
     * 9. Participants
     *
     * This method is intentionally debounced to one DOM pass per animation
     * frame to reduce flicker/repaint pressure during bursts of actions.
     */
    sortThumbnails() {
        if (pendingSortHandle) {
            return;
        }

        const runSort = () => {
            pendingSortHandle = null;
            this._sortThumbnailsNow();
        };

        if (typeof window.requestAnimationFrame === 'function') {
            pendingSortHandle = {
                type: 'raf',
                id: window.requestAnimationFrame(runSort)
            };

            return;
        }

        pendingSortHandle = {
            type: 'timeout',
            id: window.setTimeout(runSort, 16)
        };
    },

    /**
     * Performs the actual thumbnail sorting work immediately.
     *
     * @private
     * @returns {void}
     */
    _sortThumbnailsNow() {
        const pipDocument = window.documentPictureInPicture?.window?.document;
        const pipContainer = pipDocument?.getElementById('filmstripRemoteVideosContainer');
        const targetDocument = pipDocument && pipContainer ? pipDocument : document;
        const remoteVideosContainer = targetDocument.getElementById('filmstripRemoteVideosContainer');
        const localVideoContainer = targetDocument.getElementById('localVideoTileViewContainer');
        const sharedThumbnailContainer = targetDocument.getElementById('sharedThumbnailContainer');
        const state = APP.store.getState();
        const filmstripView = state['features/base/conference']?.filmStripView;
        const isStageView = filmstripView === LAYOUTS.STAGE_VIEW;
        const participants = state['features/base/participants'] || [];
        const participantswithvideos = participantWithVideoUnmuted(state) || [];
        const participantswithaudios = participantWithAudioUnmuted(state) || [];
        const participantCount = participants.length;
        const sharedThumbnailEnabled = isSharedThumbnailEnabled(state);
        const tileLimit = state['features/filmstrip']?.tileViewTileLimit;
        const tileViewTileLimit = sharedThumbnailEnabled ? tileLimit - 1 : tileLimit;
        const participantswithvideosIds = participantswithvideos.map(p => p.id);
        const participantswithaudiosIds = participantswithaudios.map(p => p.id);
        const recorder = state['features/base/config'].iAmRecorder;

        if (!remoteVideosContainer || !remoteVideos) {
            return;
        }


        const participantMap = new Map(participants.map(p => [ p.id, p ]));

        // If tileViewTileLimit is set and > 0 use it, otherwise show all participants
        const participantLimit = typeof tileViewTileLimit === 'number' && tileViewTileLimit > 0
            ? Math.max(0, tileViewTileLimit - 1) // keep previous behavior of subtracting one when limit exists
            : participantCount;

        // Helper which will try to resolve participant info from map or from conference
        const getParticipantSafe = id =>
            participantMap.get(id)
            || APP.conference.getParticipantById(id)
            || {
                id,
                name: id
            };

        // Deterministic comparator used for initial sort (dominant speaker not prioritized)
        const baseComparator = (a, b, options = {}) => {
            const {
                prioritizeDominant = false,
                prioritizePin = false,
                prioritizeMic = false,
                prioritizeCamera = false,
                prioritizeRaizeHand = false
            } = options;

            const idA = a.getId();
            const idB = b.getId();

            if (idA === idB) {
                return 0;
            }

            const participantA = getParticipantSafe(idA);
            const participantB = getParticipantSafe(idB);

            if (!participantA && participantB) {
                return 1;
            }
            if (!participantB && participantA) {
                return -1;
            }

            const isModeratorPinnedA = Boolean(participantA.moderatorPin);
            const isModeratorPinnedB = Boolean(participantB.moderatorPin);

            if (isModeratorPinnedA !== isModeratorPinnedB) {
                return isModeratorPinnedA ? -1 : 1;
            }

            const isSharedVideoA = Boolean(participantA.isFakeParticipant
                && (participantA.name === YOUTUBE_PLAYER_PARTICIPANT_NAME || participantA.name === VIDEO_PLAYER_PARTICIPANT_NAME));
            const isSharedVideoB = Boolean(participantB.isFakeParticipant
                && (participantB.name === YOUTUBE_PLAYER_PARTICIPANT_NAME || participantB.name === VIDEO_PLAYER_PARTICIPANT_NAME));

            if (isSharedVideoA !== isSharedVideoB) {
                return isSharedVideoA ? -1 : 1;
            }


            const SharedThumbnail = Boolean(sharedThumbnailContainer);

            if (SharedThumbnail && recorder) {
                return -1;
            }

            const isScreensharingA = Boolean(participantA.isFakeParticipant === 'LocalScreenShare'
                || participantA.isFakeParticipant === 'RemoteScreenShare');
            const isScreensharingB = Boolean(participantB.isFakeParticipant === 'LocalScreenShare'
                || participantB.isFakeParticipant === 'RemoteScreenShare');

            if (isScreensharingA !== isScreensharingB) {
                return isScreensharingA ? -1 : 1;
            }

            if (prioritizePin) { // Changed from options.prioritizePin
                const isPinnedA = Boolean(participantA.pinned);
                const isPinnedB = Boolean(participantB.pinned);

                if (isPinnedA !== isPinnedB) {
                    return isPinnedA ? -1 : 1;
                }
            }

            if (prioritizeDominant) { // Changed from options.prioritizeDominant
                const isDominantSpeakerA = Boolean(participantA.dominantSpeaker);
                const isDominantSpeakerB = Boolean(participantB.dominantSpeaker);

                if (isDominantSpeakerA !== isDominantSpeakerB) {
                    return isDominantSpeakerA ? -1 : 1;
                }
            }

            if (prioritizeRaizeHand) {
                const hasRaisedHandA = Boolean(participantA.raisedHand);
                const hasRaisedHandB = Boolean(participantB.raisedHand);

                if (hasRaisedHandA !== hasRaisedHandB) {
                    return hasRaisedHandA ? -1 : 1;
                }

                if (hasRaisedHandA && hasRaisedHandB) {
                    const tsA = participantA.raisedHandTimestamp || 0;
                    const tsB = participantB.raisedHandTimestamp || 0;

                    if (tsA !== tsB) {
                        return tsA - tsB;
                    }
                }
            }

            const isModeratorA = participantA.role === 'moderator';
            const isModeratorB = participantB.role === 'moderator';

            if (isModeratorA !== isModeratorB) {
                return isModeratorA ? -1 : 1;
            }

            if (prioritizeCamera) { // Changed from options.prioritizeCamera
                const hasVideoA = participantswithvideosIds.includes(idA);
                const hasVideoB = participantswithvideosIds.includes(idB);

                if (hasVideoA !== hasVideoB) {
                    return hasVideoA ? -1 : 1;
                }
            }

            if (prioritizeMic) { // Changed from options.prioritizeMic
                const hasAudioA = participantswithaudiosIds.includes(idA);
                const hasAudioB = participantswithaudiosIds.includes(idB);

                if (hasAudioA !== hasAudioB) {
                    return hasAudioA ? -1 : 1;
                }
            }

            return idA.localeCompare(idB);
        };

        // First pass: sort without considering dominant speaker
        const sortedVideos = Object.values(remoteVideos).sort((a, b) => baseComparator(a, b, {
            prioritizeDominant: false,
            prioritizePin: false,
            prioritizeMic: false,
            prioritizeCamera: false,
            prioritizeRaizeHand: false
        }));

        // Find dominant speaker's position after initial sort
        const dominantSpeakerIndex = sortedVideos.findIndex((video, index) => {
            if (index < participantLimit) {

                return false;
            }
            const participant = getParticipantSafe(video.getId());


            return Boolean(participant.dominantSpeaker);
        });

        const pinParticipantIndex = sortedVideos.findIndex(video => {
            const participant = getParticipantSafe(video.getId());

            return Boolean(participant.pinned);
        });


        const hasRaisedHandBeyoundLimit = sortedVideos.some((video, index) => {
            if (index < participantLimit) {
                return false;
            }
            const participant = getParticipantSafe(video.getId());

            return Boolean(participant?.raisedHand);
        });


        const needsDominantPriority = dominantSpeakerIndex !== -1 && dominantSpeakerIndex >= participantLimit;
        const needsPinPriority = pinParticipantIndex !== -1 && pinParticipantIndex >= participantLimit;

        if (needsDominantPriority || needsPinPriority || hasRaisedHandBeyoundLimit) {
            const visibleVideos = sortedVideos.slice(0, participantLimit);
            const hiddenVideos = sortedVideos.slice(participantLimit);

            hiddenVideos.sort((a, b) =>
                baseComparator(a, b, {
                    prioritizeDominant: needsDominantPriority,
                    prioritizePin: needsPinPriority,
                    prioritizeRaizeHand: hasRaisedHandBeyoundLimit
                }));

            const toPromote = [];

            for (let i = 0; i < hiddenVideos.length; i++) {
                const video = hiddenVideos[i];
                const id = video.getId();
                const participant = getParticipantSafe(id);

                const shouldPromote
                    = (needsDominantPriority && participant.dominantSpeaker)
                    || (needsPinPriority && participant.pinned)
                    || (hasRaisedHandBeyoundLimit && participant.raisedHand);

                if (shouldPromote) {
                    toPromote.push(video);
                }
            }

            const remainingHidden = hiddenVideos.filter(v => !toPromote.includes(v));

            const demotable = [];

            for (let i = visibleVideos.length - 1; i >= 0 && demotable.length < toPromote.length; i--) {
                const video = visibleVideos[i];
                const id = video.getId();
                const participant = getParticipantSafe(id);
                const hasPriority
                    = participant.moderatorPin
                    || participant.isFakeParticipant === 'LocalScreenShare'
                    || participant.isFakeParticipant === 'RemoteScreenShare'
                    || (participant.isFakeParticipant
                        && (participant.name === YOUTUBE_PLAYER_PARTICIPANT_NAME || participant.name === VIDEO_PLAYER_PARTICIPANT_NAME))
                    || participant.dominantSpeaker
                    || participant.pinned
                    || participant.raisedHand
                    || participant.role === 'moderator';

                if (!hasPriority) {
                    demotable.unshift(video);
                }
            }

            const swapCount = Math.min(toPromote.length, demotable.length);

            if (swapCount > 0) {
                const remainingVisible = visibleVideos.filter(v => !demotable.slice(0, swapCount).includes(v));

                const newVisible = [ ...remainingVisible, ...toPromote.slice(0, swapCount) ];

                const newHidden = [ ...demotable.slice(0, swapCount), ...remainingHidden, ...toPromote.slice(swapCount) ];

                sortedVideos.length = 0;
                sortedVideos.push(...newVisible, ...newHidden);
            }
        }

        // Remove existing "more participants" div if present
        const existingMoreDiv = targetDocument.getElementById('more-participants');

        if (existingMoreDiv && remoteVideosContainer.contains(existingMoreDiv)) {
            remoteVideosContainer.removeChild(existingMoreDiv);
        }

        // Update video elements visibility and playback
        let visibleCount = 0;

        sortedVideos.forEach((video, index) => {
            const videoElement = video.container;
            const participantId = video.getId();
            const videoTag = videoElement.querySelector('video');

            const videoTrack = getTrackByMediaTypeAndParticipant(
                state['features/base/tracks'],
                MEDIA_TYPE.VIDEO,
                participantId
            );

            const isVisible = index < participantLimit && !isStageView;
            const wasVisible = thumbnailVisibilityState.get(participantId);

            if (isVisible) {
                if (videoTrack && !videoTrack.muted && !videoTag) {
                    video.addRemoteStreamElement(videoTrack.mhTrack);
                }

                if (videoTag && wasVisible !== true) {
                    videoTag.play().catch(err => {
                        logger.warn('Failed to play video:', err);
                    });
                }
            } else if (videoTag && wasVisible !== false) {
                videoTag.pause();
            }

            thumbnailVisibilityState.set(participantId, isVisible);

            // Reorder in DOM only if position has actually changed.
            if (isVisible) {
                const targetNode = remoteVideosContainer.children[visibleCount] || null;

                if (targetNode !== videoElement) {
                    remoteVideosContainer.insertBefore(videoElement, targetNode);
                }
                visibleCount++;
            } else if (videoElement.parentElement === remoteVideosContainer) {
                if (remoteVideosContainer.lastChild !== videoElement) {
                    remoteVideosContainer.appendChild(videoElement);
                }
            }
        });

        // Add "more participants" indicator if needed (only when a numeric limit is set and not in stage view)
        if (!isStageView && typeof tileViewTileLimit === 'number' && tileViewTileLimit > 0 && participantCount > tileViewTileLimit) {
            const moreParticipants = participantCount - tileViewTileLimit;
            const moreParticipantsDiv = document.createElement('span');

            moreParticipantsDiv.id = 'more-participants';
            moreParticipantsDiv.className = 'videocontainer';
            moreParticipantsDiv.innerHTML = `
            <div id='more-participants-container'
                style="width: 100%; height: 100%; cursor: pointer; background: #1F1F1F; border-radius: 20px; display: flex; align-items: center; justify-content: center;">
                <span style="font-size: 20px; cursor: pointer; color: #fff;">+${moreParticipants} more<br>participants</span>
            </div>`;

            remoteVideosContainer.appendChild(moreParticipantsDiv);
        }


        if (!recorder) {
            if (sharedThumbnailContainer && localVideoContainer?.contains(sharedThumbnailContainer)) {
                localVideoContainer.appendChild(sharedThumbnailContainer);
            }
        }

        this.updateAllZoomTransforms();
    },

    addRemoteVideoContainer(id, remoteVideo) {

        remoteVideos[id] = remoteVideo;

        // Initialize the view
        remoteVideo.updateView();
        this.sortThumbnails();
    },

    /**
     * On video muted event.
     */
    onVideoMute(id) {
        if (APP.conference.isLocalId(id)) {
            localVideoThumbnail && localVideoThumbnail.updateView();
        } else {
            const remoteVideo = remoteVideos[id];

            if (remoteVideo) {
                remoteVideo.updateView();
            }
        }

        // large video will show avatar instead of muted stream
        this._updateLargeVideoIfDisplayed(id, true);
    },

    /**
     * Shows/hides warning about a user's connectivity issues.
     *
     * @param {string} id - The ID of the remote participant(MUC nickname).
     * @returns {void}
     */
    onParticipantConnectionStatusChanged(id) {
        if (APP.conference.isLocalId(id)) {

            return;
        }

        // We have to trigger full large video update to transition from
        // avatar to video on connectivity restored.
        this._updateLargeVideoIfDisplayed(id, true);

        const remoteVideo = remoteVideos[id];

        if (remoteVideo) {
            remoteVideo.updateView();
        }
    },

    /**
     * On last N change event.
     *
     * @param endpointsLeavingLastN - The list currently leaving last N
     * endpoints.
     * @param endpointsEnteringLastN - The list currently entering last N
     * endpoints.
     */
    onLastNEndpointsChanged(endpointsLeavingLastN, endpointsEnteringLastN) {
        if (endpointsLeavingLastN) {
            endpointsLeavingLastN.forEach(this._updateRemoteVideo, this);
        }

        if (endpointsEnteringLastN) {
            endpointsEnteringLastN.forEach(this._updateRemoteVideo, this);
        }
    },

    /**
     * Updates remote video by id if it exists.
     *
     * @param {string} id - Of the remote video.
     * @private
     */
    _updateRemoteVideo(id) {
        const remoteVideo = remoteVideos[id];

        if (remoteVideo) {
            remoteVideo.updateView();
            this._updateLargeVideoIfDisplayed(id);
        }
    },

    removeParticipantContainer(id) {
        // Unlock large video
        if (this.getPinnedId() === id) {
            // console.info('Focused video owner has left the conference');
            APP.store.dispatch(pinParticipant(null));
        }

        const remoteVideo = remoteVideos[id];

        if (remoteVideo) {
            // Remove remote video
            //  logger.info(`Removing remote video: ${id}`);
            delete remoteVideos[id];
            thumbnailVisibilityState.delete(id);
            remoteVideo.remove();
            this.sortThumbnails();
        } else {
            logger.warn(`No remote video for ${id}`);
        }
    },

    onVideoTypeChanged(id) {
        const remoteVideo = remoteVideos[id];

        if (!remoteVideo) {
            return;
        }

        //  logger.info('Peer video type changed: ', id, newVideoType);
        remoteVideo.updateView();
    },

    /**
     * Resizes the video area.
     */
    resizeVideoArea() {
        if (largeVideo) {
            largeVideo.updateContainerSize();
            largeVideo.resize(false);
            this.sortThumbnails();
            this.updateAllZoomTransforms();
        }
    },

    /**
     * Returns all active thumbnail instances.
     *
     * @returns {Array}
     */
    getAllThumbnails() {
        return getAllThumbnails();
    },

    /**
     * Updates the zoom transform on all thumbnails.
     *
     * @returns {void}
     */
    updateAllZoomTransforms() {
        getAllThumbnails().forEach(thumb => {
            thumb?.updateZoomTransform?.();
        });
    },

    getLocalVideo() {
        return localVideoThumbnail;
    },

    getSmallVideo(id) {
        if (!id || (APP.conference && APP.conference.isLocalId(id))) {
            return localVideoThumbnail;
        }

        const localPart = getLocalParticipant();

        if (localPart && localPart.id === id) {
            return localVideoThumbnail;
        }

        return remoteVideos[id];

    },

    changeUserAvatar(id, avatarUrl) {
        if (this.isCurrentlyOnLarge(id)) {
            largeVideo.updateAvatar(avatarUrl);
        }
    },

    isLargeVideoVisible() {
        return this.isLargeContainerTypeVisible(VIDEO_CONTAINER_TYPE);
    },

    /**
     * @returns {LargeContainer} The currently displayed container on large
     * video.
     */
    getCurrentlyOnLargeContainer() {
        return largeVideo.getCurrentContainer();
    },

    isCurrentlyOnLarge(id) {
        return largeVideo && largeVideo.id === id;
    },

    /**
     * Triggers an update of remote video and large video displays so they may
     * pick up any state changes that have occurred elsewhere.
     *
     * @returns {void}
     */
    updateAllVideos() {
        const displayedUserId = this.getLargeVideoID();

        if (displayedUserId) {
            this.updateLargeVideo(displayedUserId, true);
        }

        Object.keys(remoteVideos).forEach(video => {
            remoteVideos[video].updateView();
        });
    },

    /**
     * Updates tile view classes for all SmallVideo instances when participants count
     * or conference view changes. This ensures immediate visual updates.
     *
     * @returns {void}
     */
    updateAllTileViewClasses() {
        // Update local video tile view class
        if (localVideoThumbnail) {
            localVideoThumbnail.updateTileViewClass();
        }

        // Update all remote video tile view classes
        Object.keys(remoteVideos).forEach(video => {
            remoteVideos[video].updateTileViewClass();
        });
    },

    /**
     * Triggers state change updates for all SmallVideo instances.
     * This method should be called when participants count or conference view changes.
     *
     * @returns {void}
     */
    onParticipantsOrViewStateChange() {
        // Update local video
        if (localVideoThumbnail) {
            localVideoThumbnail.onStateChange();
        }

        // Update all remote videos
        Object.keys(remoteVideos).forEach(video => {
            remoteVideos[video].onStateChange();
        });

        this.sortThumbnails();
    },

    updateLargeVideo(id, forceUpdate) {
        if (!largeVideo) {
            return;
        }
        const currentContainer = largeVideo.getCurrentContainer();
        const currentContainerType = largeVideo.getCurrentContainerType();
        const isOnLarge = this.isCurrentlyOnLarge(id);
        const state = APP.store.getState();
        const participant = getParticipantById(state, id);
        const isFakeScreenshare = participant?.isFakeParticipant === 'LocalScreenShare' || participant?.isFakeParticipant === 'RemoteScreenShare';

        // For screenshares, the fake participant ID is the source name (e.g., 'abc123-v1')
        // and the track is also stored with the source name as participantId
        // So we use the ID directly without modification
        const videoTrack = getTrackByMediaTypeAndParticipant(state['features/base/tracks'],
            isFakeScreenshare ? MEDIA_TYPE.SCREENSHARE : MEDIA_TYPE.VIDEO, id);
        const videoStream = videoTrack?.mhTrack;

        let shouldForceUpdate = forceUpdate;

        if (isOnLarge && !shouldForceUpdate
                && LargeVideoManager.isVideoContainer(currentContainerType)
                && videoStream) {
            const currentStreamId = currentContainer.getStreamID();
            const newStreamId = videoStream?.getId() || null;

            // FIXME it might be possible to get rid of 'forceUpdate' argument
            if (currentStreamId !== newStreamId) {
                logger.debug('Enforcing large video update for stream change');
                shouldForceUpdate = true;
            }
        }

        if (!isOnLarge || shouldForceUpdate) {
            const videoType = this.getRemoteVideoType(id);

            // console.log(id, 'id', videoTrack, 'VideoTrack', videoType, 'videoType');
            largeVideo.updateLargeVideo(
                id,
                videoStream,
                videoType || VIDEO_TYPE.CAMERA
            ).catch(() => {
                // do nothing
            });
        }
    },

    addLargeVideoContainer(type, container) {
        largeVideo && largeVideo.addContainer(type, container);
    },

    removeLargeVideoContainer(type) {
        largeVideo && largeVideo.removeContainer(type);
    },

    /**
     * @returns Promise.
     */
    showLargeVideoContainer(type, show) {
        if (!largeVideo) {
            return Promise.reject();
        }

        const isVisible = this.isLargeContainerTypeVisible(type);

        if (isVisible === show) {
            return Promise.resolve();
        }

        const currentId = largeVideo.id;
        let oldSmallVideo;

        if (currentId) {
            oldSmallVideo = this.getSmallVideo(currentId);
        }

        let containerTypeToShow = type;

        // if we are hiding a container and there is focusedVideo
        // (pinned remote video) use its video type,
        // if not then use default type - large video

        if (!show) {
            const pinnedId = this.getPinnedId();

            if (pinnedId) {
                // console.log('getRemoteVideoType', pinnedId);

                containerTypeToShow = this.getRemoteVideoType(pinnedId);
            } else {
                containerTypeToShow = VIDEO_CONTAINER_TYPE;
            }
        }

        return largeVideo.showContainer(containerTypeToShow)
            .then(() => {
                if (oldSmallVideo) {
                    oldSmallVideo && oldSmallVideo.updateView();
                }
            });
    },

    isLargeContainerTypeVisible(type) {
        return largeVideo && largeVideo.state === type;
    },

    /**
     * Returns the id of the current video shown on large.
     * Currently used by tests (torture).
     */
    getLargeVideoID() {
        return largeVideo && largeVideo.id;
    },

    /**
     * Returns the the current video shown on large.
     * Currently used by tests (torture).
     */
    getLargeVideo() {
        return largeVideo;
    },

    /**
     * Sets the flipX state of the local video.
     *
     * @param {boolean} true - For flipped otherwise false;.
     */
    setLocalFlipX(val) {
        this.localFlipX = val;
        onLocalFlipXChanged(val);
        if (localVideoThumbnail) {
            localVideoThumbnail.setFlipX(val);
        }
    },

    /** ........
     * Returns the wrapper jquery selector for the largeVideo
     *
     * @returns {JQuerySelector} the wrapper jquery selector for the largeVideo
     */
    getLargeVideoWrapper() {
        return this.getCurrentlyOnLargeContainer().$wrapper;
    },

    /**
     * Returns the number of remove video ids.
     *
     * @returns {number} The number of remote videos.
     */
    getRemoteVideosCount() {
        return Object.keys(remoteVideos).length;
    },

    /**
     * Helper method to invoke when the video layout has changed and elements
     * have to be re-arranged and resized.
     *
     * @returns {void}
     */
    refreshLayout() {
        localVideoThumbnail && localVideoThumbnail.updateDOMLocation();
        VideoLayout.resizeVideoArea();
        this.sortThumbnails();

        // Rerender the thumbnails since they are dependant on the layout because of the tooltip positioning.
        localVideoThumbnail && localVideoThumbnail.rerender();
        Object.values(remoteVideos).forEach(remoteVideoThumbnail => remoteVideoThumbnail.rerender());
    },

    /**
     * Cleans up any existing largeVideo instance.
     *
     * @private
     * @returns {void}
     */
    _resetLargeVideo() {
        if (largeVideo) {
            largeVideo.destroy();
        }

        largeVideo = null;
    },

    /**
     * Cleans up filmstrip state. While a separate {@code Filmstrip} exists, its
     * implementation is mainly for querying and manipulating the DOM while
     * state mostly remains in {@code VideoLayout}.
     *
     * @private
     * @returns {void}
     */
    _resetFilmstrip() {
        if (pendingSortHandle) {
            if (pendingSortHandle.type === 'raf') {
                window.cancelAnimationFrame(pendingSortHandle.id);
            } else {
                window.clearTimeout(pendingSortHandle.id);
            }
            pendingSortHandle = null;
        }

        thumbnailVisibilityState.clear();

        Object.keys(remoteVideos).forEach(remoteVideoId => {
            this.removeParticipantContainer(remoteVideoId);
            delete remoteVideos[remoteVideoId];
        });

        if (localVideoThumbnail) {
            localVideoThumbnail.remove();
            localVideoThumbnail = null;
        }
    },

    /**
     * Triggers an update of large video if the passed in participant is
     * currently displayed on large video.
     *
     * @param {string} participantId - The participant ID that should trigger an
     * update of large video if displayed.
     * @param {boolean} force - Whether or not the large video update should
     * happen no matter what.
     * @returns {void}
     */
    _updateLargeVideoIfDisplayed(participantId, force = false) {
        if (this.isCurrentlyOnLarge(participantId)) {
            this.updateLargeVideo(participantId, force);
        }
    },

    /**
     * Handles window resizes.
     */
    onResize() {
        VideoLayout.resizeVideoArea();
    }
};

export default VideoLayout;
